import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Volume2, Piano, Sliders, Music4, Share2, AlertTriangle } from 'lucide-react';
import { PlayableKeyboard } from '@/components/microtonal/PlayableKeyboard';
import { KeyboardEditor } from '@/components/microtonal/KeyboardEditor';
import { PianoRollEditor } from '@/components/microtonal/PianoRollEditor';
import { ShareKeyboards } from '@/components/microtonal/ShareKeyboards';
import { useMicrotonalLibrary } from '@/hooks/useMicrotonalLibrary';
import { useAudioEngine } from '@/hooks/useAudioEngine';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function Index() {
  useSeoMeta({
    title: 'Microtonal Forge — Playable Microtonal Keyboard & MIDI Editor',
    description: 'Create, play, and share microtonal keyboards and scales. Export microtonal MIDI files with pitch-bend per channel. Supports 16 channels, 128 instruments, and any tuning system including Bohlen-Pierce.',
  });

  const { allKeyboards, activeKeyboardId, setActiveKeyboardId, activeKeyboard } = useMicrotonalLibrary();
  const { volume, setVolume, waveform, setWaveform, octaveShift, setOctaveShift } = useAudioEngineWithControls();
  const [tab, setTab] = useState('play');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎵</span>
                <h1 className="text-xl font-bold tracking-tight">Microtonal Forge</h1>
              </div>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Vibed with <a href="https://marlowe.shakespeare.wtf" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Marlowe</a>
              </span>
            </div>

            <div className="flex items-center gap-4">
              {/* Keyboard selector */}
              <Select value={activeKeyboardId} onValueChange={setActiveKeyboardId}>
                <SelectTrigger className="w-48 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Built-in Keyboards</SelectLabel>
                    {allKeyboards.filter((k) => k.builtIn).map((k) => (
                      <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                    ))}
                  </SelectGroup>
                  {allKeyboards.some((k) => !k.builtIn) && (
                    <SelectGroup>
                      <SelectLabel>My Keyboards</SelectLabel>
                      {allKeyboards.filter((k) => !k.builtIn).map((k) => (
                        <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>

              {/* Volume control */}
              <div className="flex items-center gap-2">
                <Volume2 className="size-4 text-muted-foreground" />
                <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} max={1} step={0.01} className="w-20" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-4 max-w-2xl mb-6">
            <TabsTrigger value="play"><Piano className="size-4 mr-1" /> Play</TabsTrigger>
            <TabsTrigger value="edit"><Sliders className="size-4 mr-1" /> Edit</TabsTrigger>
            <TabsTrigger value="midi"><Music4 className="size-4 mr-1" /> MIDI</TabsTrigger>
            <TabsTrigger value="share"><Share2 className="size-4 mr-1" /> Share</TabsTrigger>
          </TabsList>

          {/* Play tab */}
          <TabsContent value="play" className="space-y-6">
            <Alert>
              <AlertTriangle className="size-4" />
              <AlertDescription>
                <strong>Speaker-safe audio:</strong> Volume is capped at 35% max gain with a compressor limiter. 
                Tones use smooth 8ms attack ramps to prevent clicking. Sine waveform is recommended for small speakers.
              </AlertDescription>
            </Alert>

            {/* Control bar */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Waveform:</span>
                <Select value={waveform} onValueChange={(v) => setWaveform(v as 'sine' | 'triangle' | 'square' | 'sawtooth')}>
                  <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sine">Sine (safe)</SelectItem>
                    <SelectItem value="triangle">Triangle</SelectItem>
                    <SelectItem value="square">Square</SelectItem>
                    <SelectItem value="sawtooth">Sawtooth</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Octave:</span>
                <Button variant="outline" size="sm" onClick={() => setOctaveShift(octaveShift - 1)}>−</Button>
                <span className="text-sm w-8 text-center">{octaveShift >= 0 ? '+' : ''}{octaveShift}</span>
                <Button variant="outline" size="sm" onClick={() => setOctaveShift(octaveShift + 1)}>+</Button>
              </div>

              <div className="text-sm text-muted-foreground">
                {activeKeyboard?.scale.name} · {activeKeyboard?.scale.steps.length + 1} degrees per period
              </div>
            </div>

            {/* Playable keyboard */}
            <div className="rounded-xl border bg-card p-6">
              <div className="overflow-x-auto pb-2">
                <PlayableKeyboard keyboard={activeKeyboard} octaveShift={octaveShift} />
              </div>
            </div>

            <div className="text-sm text-muted-foreground max-w-2xl">
              <p className="mb-2">
                <strong>How to play:</strong> Click/tap the keys, or use your QWERTY keyboard. 
                Bottom row keys (A, S, D, F…) play the main scale degrees. 
                Top row keys (Z, X, C, V…) play the offset degrees.
              </p>
              <p>
                <strong>Current scale:</strong> {activeKeyboard?.scale.name} — 
                Period: {activeKeyboard?.scale.periodCents} cents 
                ({activeKeyboard?.scale.periodCents === 1200 ? 'octave' : activeKeyboard?.scale.periodCents === 1901.955 ? 'tritave (3/1)' : 'custom'}) · 
                Base: {activeKeyboard?.scale.baseFrequency} Hz
              </p>
            </div>
          </TabsContent>

          {/* Edit tab */}
          <TabsContent value="edit">
            <KeyboardEditor />
          </TabsContent>

          {/* MIDI tab */}
          <TabsContent value="midi">
            <PianoRollEditor keyboard={activeKeyboard} />
          </TabsContent>

          {/* Share tab */}
          <TabsContent value="share">
            <ShareKeyboards />
          </TabsContent>
        </Tabs>
      </main>

      <footer className="border-t mt-12 py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>
            Microtonal Forge — Create, play, and share microtonal music over Nostr.{' '}
            Vibed with <a href="https://marlowe.shakespeare.wtf" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Marlowe</a>.
          </p>
        </div>
      </footer>
    </div>
  );
}

/** Small helper hook for octave shift state (local to the page). */
function useAudioEngineWithControls() {
  const audio = useAudioEngine();
  const [octaveShift, setOctaveShift] = useState(0);
  return { ...audio, octaveShift, setOctaveShift };
}
